import * as React from 'react';

import {useTileData} from 'src/components/analytics-genesis/byod/api';
import {ByodErrorBoundary} from 'src/components/analytics-genesis/byod/components/byod-error-boundary';
import {
  DashboardTile,
  UnavailableTile,
} from 'src/components/analytics-genesis/byod/components/dashboard-tile';
import type {FetchDataParams} from 'src/components/analytics-genesis/byod/components/dashboard-tile';
import {buildTableSearchConfig} from 'src/components/analytics-genesis/byod/components/byod-table-filter';
import type {TableSearch} from 'src/components/analytics-genesis/byod/components/byod-table-filter';
import {ByodPlacementSchedulePanel} from 'src/components/analytics-genesis/byod/components/byod-schedule-panel';
import {DEFAULT_PAGE_SIZE} from 'src/components/analytics-genesis/byod/constants';
import {resolveTileFilters} from 'src/components/analytics-genesis/byod/utils/resolve-tile-filters';
import {isInternalUser} from 'src/utils/check-internal-user';
import type {WidgetConfig, ChartStyle} from 'src/types/byod';
import {WIDGET_TYPE, isKpiWidgetType} from 'src/types/byod';

import {useCanvasContext} from './canvas-context';


export type CanvasTileProps = {
  tileId: string;
  /** True while this tile is animating out (builder remove). */
  isRemoving?: boolean;
  /**
   * Force read-only regardless of `editable` — used by the DragOverlay clone so
   * the floating copy never shows edit menus.
   */
  forceReadOnly?: boolean;
};

/**
 * Controlled per-tile adapter. Reads the widget + injected filters + change
 * handlers from `CanvasContext` (no store), fetches via the shared
 * `useTileData`, and renders the shared `<DashboardTile>`. The builder and the
 * embed both go through this, so a tile/style change reflects in both.
 */
export const CanvasTile = React.memo(
  ({tileId, isRemoving = false, forceReadOnly = false}: CanvasTileProps) => {
    const ctx = useCanvasContext();
    const widget = ctx.widgets[tileId];
    const wt = widget?.widget_type;
    if (!widget || !wt) {
      return null;
    }
    const editable = forceReadOnly ? false : ctx.editable;
    // KPI uses the compact KPI layout; everything else is a viz.
    const tileKind = isKpiWidgetType(wt) ? 'kpi' : 'viz';
    return (
      <ByodErrorBoundary variant="tile" tileKind={tileKind} resetKey={tileId}>
        {widget.is_orphaned ? (
          // Catalog definition was deleted — render a gentle "unavailable"
          // state instead of querying a widget the backend can no longer serve.
          <UnavailableTile
            tileId={tileId}
            label={widget.label}
            tileKind={tileKind}
          />
        ) : (
          <CanvasTileInner
            tileId={tileId}
            widget={widget}
            editable={editable}
            isRemoving={isRemoving}
          />
        )}
      </ByodErrorBoundary>
    );
  },
);

function CanvasTileInner({
  tileId,
  widget,
  editable,
  isRemoving,
}: {
  tileId: string;
  widget: WidgetConfig;
  editable: boolean;
  isRemoving: boolean;
}) {
  const {
    dashboardId,
    filters: injectedFilters,
    onEditWidget,
    onRemoveWidget,
    onUpdateWidget,
    onExport,
  } = useCanvasContext();

  const isTable = widget.widget_type === WIDGET_TYPE.TABLE;

  // Pagination/sort is a VIEW operation — it lives in ephemeral local state so
  // it works even when read-only (and so multiple instances don't fight over
  // the store). When editable, it ALSO persists via onUpdateWidget so a save
  // captures it.
  const [tableParams, setTableParams] = React.useState<FetchDataParams>({
    offset: 0,
    limit: DEFAULT_PAGE_SIZE,
  });

  // View-time search (session-only lens — not persisted to the dashboard). The
  // applied search folds into the tile's /query `widget_config` and re-fetches;
  // `tableFilterResetKey` snaps the table footer back to page 1.
  const [search, setSearch] = React.useState<TableSearch>({});
  const [tableFilterResetKey, setTableFilterResetKey] = React.useState(0);

  const [isSchedulerOpen, setIsSchedulerOpen] = React.useState(false);

  const handleFetchData = React.useCallback(
    (params: FetchDataParams) => {
      setTableParams(params);
      if (editable && onUpdateWidget) {
        onUpdateWidget(tileId, {
          limit: params.limit,
          offset: params.offset,
          sort_by: params.sort_by,
          sort_direction: params.sort_direction,
        });
      }
    },
    [editable, onUpdateWidget, tileId],
  );

  // Effective widget folds live pagination + the view-time search into the
  // config that drives the query; the ORIGINAL widget still drives initial sort
  // / columns / badge. Column overrides are already merged into `widget`
  // upstream (the embed's dashboardViewStore, the builder's store).
  const queryWidget = React.useMemo<WidgetConfig>(() => {
    if (!isTable) {
      return widget;
    }
    return {
      ...widget,
      widget_config_json: {
        ...widget.widget_config_json,
        ...tableParams,
        ...buildTableSearchConfig(search),
      },
    };
  }, [isTable, widget, tableParams, search]);

  // Effective /query filters, memoized so it only re-allocates when an input
  // changes: the tile's saved filters (converted to wire shape) with the
  // canvas-injected filters projected + layered on top. See resolveTileFilters.
  const filters = React.useMemo(
    () =>
      resolveTileFilters(
        widget.widget_filters_json,
        injectedFilters,
        widget.supported_filters,
      ),
    [widget.widget_filters_json, injectedFilters, widget.supported_filters],
  );

  const {ref, data, isError, isFetching, error, refetch, fetchStatus} =
    useTileData({
      placementId: tileId,
      widget: queryWidget,
      dashboardId,
      filters,
    });

  const handleChartStyleChange = React.useCallback(
    (kind: ChartStyle) => {
      onUpdateWidget?.(tileId, {chart_style: kind});
    },
    [onUpdateWidget, tileId],
  );

  // Column selection is a view-level edit routed through onUpdateWidget: the
  // builder persists it to the dashboard, the embed writes it to its local
  // per-session store. Either way the widget the tile reads is already updated
  // upstream — so this works read-only, unlike edit/remove/chart-style below.
  const handleColumnsChange = React.useCallback(
    (cols: string[]) => {
      onUpdateWidget?.(tileId, {selected_columns: cols});
    },
    [onUpdateWidget, tileId],
  );

  const handleEntityColumnsChange = React.useCallback(
    (cols: string[]) => {
      onUpdateWidget?.(tileId, {entities_columns: cols});
    },
    [onUpdateWidget, tileId],
  );

  const handleExport = React.useMemo(
    () => (onExport ? () => onExport(tileId) : undefined),
    [onExport, tileId],
  );

  // Applying a search resets pagination to page 1 (offset 0) so the refetch
  // starts from the first page of the filtered set, then bumps the reset key so
  // the table footer follows.
  const handleSearchChange = React.useCallback((next: TableSearch) => {
    setSearch(next);
    setTableParams({offset: 0, limit: DEFAULT_PAGE_SIZE});
    setTableFilterResetKey((k) => k + 1);
  }, []);

  // Placement scheduling is offered only on a saved dashboard in view mode
  // (`!editable`), table tiles only: the tile id is a frontend-minted UUID the
  // backend only knows after Save & Publish, so scheduling an unsaved/edit-mode
  // tile would target a placement the server hasn't persisted. (The bell lives
  // in the table toolbar, so it never applies to KPI/chart tiles.)
  // Scheduling is an internal-only capability for now.
  const canSchedule =
    isTable && !editable && Boolean(dashboardId) && isInternalUser();
  const handleSchedule = React.useMemo(
    () => (canSchedule ? () => setIsSchedulerOpen(true) : undefined),
    [canSchedule],
  );

  return (
    <>
      <DashboardTile
        tileId={tileId}
        widget={widget}
        canEdit={editable}
        tileRef={ref}
        data={data}
        isError={isError}
        isFetching={isFetching}
        error={error}
        fetchStatus={fetchStatus}
        onRetry={refetch}
        isRemoving={isRemoving}
        onEdit={editable ? onEditWidget : undefined}
        onRemove={editable ? onRemoveWidget : undefined}
        onChartStyleChange={editable ? handleChartStyleChange : undefined}
        // Column editing works whenever the parent can absorb the change
        // (onUpdateWidget) — the builder persists it, the embed stores it
        // locally for the session — so it's available read-only, not gated on
        // `editable`.
        onColumnsChange={onUpdateWidget ? handleColumnsChange : undefined}
        onEntityColumnsChange={
          onUpdateWidget ? handleEntityColumnsChange : undefined
        }
        onFetchData={handleFetchData}
        onExport={handleExport}
        onSchedule={handleSchedule}
        search={isTable ? search : undefined}
        onSearchChange={isTable ? handleSearchChange : undefined}
        tableResetKey={tableFilterResetKey}
      />
      {canSchedule && isSchedulerOpen && (
        <ByodPlacementSchedulePanel
          placementId={tileId}
          reportName={widget.label ?? tileId}
          isOpen={isSchedulerOpen}
          onClose={() => setIsSchedulerOpen(false)}
        />
      )}
    </>
  );
}
